<template>
    <!-- The control, the accessible name and the attention dot are the orb's and are
         written once (AssistantOrb.vue). This component fills its picture — and where
         it cannot, passes nothing and the CSS sphere is drawn instead. -->
    <AssistantOrb
        :state="state"
        :size="size"
        :decorative="decorative"
        @click="emit('click')"
    >
        <template v-if="available" #default>
            <span class="milo-head" :style="headStyle">
                <!-- The model's own frame, larger than the circle and sitting high in
                     it, so that what the circle shows is the FACE. See HEAD_SCALE. -->
                <span class="milo-stage" :style="stageStyle">
                    <span ref="mount" class="milo-layer" />

                    <!-- The poster, inside the same frame so that the picture does not
                         jump the moment the model takes over from it. -->
                    <img
                        v-if="!loaded && !posterFailed"
                        :key="posterUrl"
                        class="milo-layer"
                        :src="posterUrl"
                        :alt="$t('assistant.head_loading')"
                        @error="onPosterError"
                    >
                </span>
            </span>
        </template>
    </AssistantOrb>
</template>

<script setup lang="ts">
    import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
    import { useI18n } from 'vue-i18n'

    import AssistantOrb from '@/components/app/AssistantOrb.vue'
    import { miloStateFor, onMiloReaction, type AssistantOrbState } from '@/libs/assistant'
    import { MILO_ASSET_BASE, loadMiloSdk, miloRendererSupported } from '@/libs/milo'
    import { useMiloGaze } from '@/libs/miloGaze'
    import type { MiloElement, MiloErrorDetail, MiloReaction } from '@/types/Milo'

    /**
     * Milo's face — the owner's animated 3D head, wherever the orb used to be.
     *
     * # What this is
     *
     * A package delivered by the owner and served by us
     * (`public/milo/1.1.0/`, see `docs/AI.md` § 20): a `<milo-assistant>` web component
     * over a pinned `@google/model-viewer` 4.3.1, a 640 KB GLB with ten clips, and a
     * poster PNG. Nothing is fetched from a CDN and nothing of it is in the app bundle
     * — `libs/milo.ts` imports it from the public folder at run time and explains why.
     *
     * This file is the package's own Vue adapter, rewritten to the house's shape. The
     * behaviour it guards is NOT house-invented and is not to be tidied away: the
     * shared load queue, the generation ticket read on both sides of an await, the
     * teardown that lets the element settle its own pending promises, and `react()`
     * waiting a tick so that a state set in the same handler reaches the element first.
     * Every one of them is a case the package's VALIDATION.md was tested against.
     *
     * # The crop
     *
     * The model is framed with a neck stub below the chin. The circle clips, and the
     * model is drawn into a frame `HEAD_SCALE` times as wide, sitting a little high in
     * it: the circle shows the WHOLE head with air around it, and only the neck stub is
     * outside. The first cut (1.4, face filling the circle) read as a filled disc at
     * 40 px — the owner asked for a head one can see is a head.
     *
     * # States and reactions
     *
     *   idle / thinking / attention  — the orb's three states, mapped in
     *                                  `libs/assistant.ts` (`attention` → Concerned).
     *   success / error / smile      — one-shot expressions, from
     *                                  `reactMilo()` on the same composable.
     *
     * Every mounted head subscribes to that bus, so both instances react together.
     *
     * # Looking at the cursor
     *
     * The head turns towards the mouse, a little. There is no bone to turn, so it is
     * done by moving the CAMERA a few degrees off the pose the package parks it in —
     * `libs/miloGaze.ts` holds the whole of it, including why it is off on a touch
     * screen and where the base pose comes from. Each mounted head measures from its
     * own centre, so both look at the same cursor rather than at the same point.
     *
     * # When there is no head
     *
     * No WebGL, or the package could not be loaded, or the model itself failed: nothing
     * fills the orb's slot and the desk sees the CSS sphere, which has been the
     * assistant's face since v1.446.0 and stays for exactly this. Reduced motion is the
     * package's own business — it holds a still pose rather than animating.
     */

    /**
     * How much larger than the visible circle the model's frame is drawn, and how much
     * of the overflow is taken off the top.
     *
     * 1.1 and a third: at the top bar's 40 px the frame is 44 px, about 1 px of it
     * above the circle and 3 px below it — the crown and the chin stay with a margin
     * of background around them, the neck goes.
     */
    const HEAD_SCALE = 1.1
    const HEAD_RISE  = 0.35

    const props = withDefaults(defineProps<{
        /** The assistant's state, in the portal's own vocabulary. */
        state?: AssistantOrbState
        /** Diameter of the visible circle in pixels. 40 in the top bar, 96 in the
         *  drawer's header. */
        size?: number
        /** A picture of Milo rather than the control — the drawer's own header. */
        decorative?: boolean
    }>(), {
        state:      'idle',
        size:       40,
        decorative: false,
    })

    const emit = defineEmits<{ click: [] }>()

    const { t } = useI18n()

    const mount        = ref<HTMLElement | null>(null)
    /** The GLB is drawn and the poster can go. */
    const loaded       = ref(false)
    const moduleFailed = ref(false)
    const posterFailed = ref(false)

    /** Turns the head towards the cursor. A no-op where the inner viewer cannot be
     *  found, so the package may change its insides without breaking the face. */
    const gaze = useMiloGaze()

    /** Asked before anything is downloaded: a machine with no WebGL must not fetch
     *  1.8 MB in order to discover it cannot use it. */
    const rendererSupported = miloRendererSupported()

    const available = computed((): boolean => rendererSupported && !moduleFailed.value)

    // Resolved the same way the element's own `src` and `poster` are, against the page
    // rather than against the bundle chunk this code is running from.
    const posterUrl = computed((): string => new URL(`${MILO_ASSET_BASE}/MILO-Poster.png`, document.baseURI).href)

    const headStyle = computed(() => ({
        width:  `${props.size}px`,
        height: `${props.size}px`,
    }))

    const stageStyle = computed(() => {
        const frame    = props.size * HEAD_SCALE
        const overflow = frame - props.size

        return {
            width:  `${frame}px`,
            height: `${frame}px`,
            left:   `${-overflow / 2}px`,
            top:    `${-overflow * HEAD_RISE}px`,
        }
    })

    let element:        MiloElement | null = null
    let removeListeners: (() => void) | null = null
    let mounted    = false
    let generation = 0

    /** Removing the element is what makes the package settle whatever it had pending
     *  and stop rendering; its own disconnect handler does that work. */
    const removeElement = () => {
        gaze.release()
        removeListeners?.()
        removeListeners = null
        element?.remove()
        element = null
        loaded.value = false
    }

    /** No head, then: the orb takes over and nothing more is attempted. */
    const giveUp = () => {
        removeElement()
        moduleFailed.value = true
    }

    const initialize = async () => {
        // The ticket a later mount, or an unmount, invalidates. Read on BOTH sides of
        // every await: a native import cannot be cancelled, so the only defence against
        // a megabyte that lands after this component is gone is to refuse to use it.
        const ticket = ++generation

        removeElement()

        const current = () => mounted && ticket === generation && mount.value !== null

        try {
            // Resolved against the PAGE, not against the hashed bundle chunk this code
            // is running from — the portal is served from the domain root today and
            // this keeps working if it ever is not.
            const owner  = mount.value!.ownerDocument
            const folder = new URL(`${MILO_ASSET_BASE}/`, owner.baseURI)

            await loadMiloSdk(new URL('milo-assistant.js', folder).href, current)

            if (!current()) return

            if (!owner.defaultView?.customElements.get('milo-assistant')) {
                throw new Error('The milo-assistant element was not registered.')
            }

            const node   = owner.createElement('milo-assistant') as MiloElement
            const active = () => current() && element === node

            element = node

            // The inner `<model-viewer>` is the package's and is created — and
            // replaced — by it, so the gaze is pointed at the head only once the
            // package says it has one.
            const onReady = () => {
                if (!active()) return

                loaded.value = node.ready
                gaze.follow(node)
            }

            const onError = (event: Event) => {
                if (!active()) return

                // A model that cannot be fetched is not a head at all. An unsupported
                // animation is: one clip is missing and the rest still plays.
                if ((event as CustomEvent<MiloErrorDetail>).detail?.reason === 'load-failed') giveUp()
            }

            node.addEventListener('milo-ready', onReady)
            node.addEventListener('milo-error', onError)

            removeListeners = () => {
                node.removeEventListener('milo-ready', onReady)
                node.removeEventListener('milo-error', onError)
            }

            // An element created in script carries no scoped-style attribute, so its
            // size is set here rather than in the block below.
            Object.assign(node.style, { display: 'block', width: '100%', height: '100%' })
            node.setAttribute('src', new URL('MILO-Head.glb', folder).href)
            node.setAttribute('poster', new URL('MILO-Poster.png', folder).href)
            node.setAttribute('label', t('assistant.title'))
            // The latest state, not the one this function started with.
            node.setState(miloStateFor(props.state))

            mount.value!.append(node)
            loaded.value = node.ready
        } catch {
            if (!current()) return

            giveUp()
        }
    }

    /**
     * Plays one expression on this head.
     *
     * The tick is the point: a caller that ends a request sets the persistent state to
     * `idle` and asks for `success` in the same handler, and the package returns to the
     * PERSISTENT state when the expression is over. Without the wait the element would
     * still be on `thinking` when the reaction was queued, and Milo would go back to
     * thinking about a request that had finished.
     */
    const react = async (reaction: MiloReaction) => {
        const ticket = generation

        await nextTick()

        if (!mounted || ticket !== generation || !element) return

        try {
            await element.react(reaction)
        } catch {
            // An expression that did not play is not worth a word to anybody.
        }
    }

    const onPosterError = (event: Event) => {
        // Only the poster this component asked for; a stale error about a URL that is
        // no longer current says nothing about the picture on screen.
        if ((event.target as HTMLImageElement).getAttribute('src') === posterUrl.value) {
            posterFailed.value = true
        }
    }

    watch(() => props.state, state => element?.setState(miloStateFor(state)))

    onMiloReaction(reaction => { void react(reaction) })

    onMounted(() => {
        mounted = true

        if (available.value) void initialize()
    })

    onBeforeUnmount(() => {
        mounted = false
        generation++
        removeElement()
    })
</script>

<style scoped>
    /* The window. Everything the model does outside it is the reason it exists. */
    .milo-head {
        position: relative;
        display: block;
        overflow: hidden;
        border-radius: 9999px;
    }

    .milo-stage {
        position: absolute;
    }

    .milo-layer {
        position: absolute;
        inset: 0;
        width: 100%;
        height: 100%;
        object-fit: contain;
    }
</style>
